import { forwardRef } from "react";

import { Chip as MaterialChip, ChipProps as MaterialChipProps, SxProps, Theme, useTheme } from "@mui/material";

import { FormIcon, FormIconProps } from "@/forms-design-system/FormIcon";
import { mergeSx } from "@/forms-design-system/helpers";
import { InvalidThemeErrorMessage } from "@/forms-design-system/InvalidThemeErrorMessage";
import { IconElement } from "@/icons/types";

declare module "@mui/material/Chip" {
  interface ChipOwnProps {
    disableRipple?: boolean;
  }
}

export type FormChipProps = Omit<
  MaterialChipProps,
  "icon" | "variant" | "color" | "disableRipple" | "onClick" | "onDelete"
> & {
  icon?: IconElement;
  variant?: "filled" | "outlined";
  vibe?: "neutral" | "danger" | "emphasis";
  components?: {
    icon?: {
      size?: FormIconProps["size"];
      sx?: SxProps<Theme>;
    };
  };
};

export const FormChip = forwardRef<HTMLDivElement, FormChipProps>(
  ({ icon, variant = "filled", vibe = "neutral", sx, components, ...rest }, ref) => {
    const theme = useTheme();

    if (!theme.palette.forms || !theme.palette.formsComponents?.chip) {
      return <InvalidThemeErrorMessage />;
    }

    const styles = mergeSx(
      {
        paddingInline: 1,
        height: 20,
        ...theme.typography.caption,
        borderRadius: theme.radius(9999),
        ...(variant === "filled" &&
          vibe === "neutral" && {
            backgroundColor: theme.palette.formsComponents.chip.neutral.filled.backgroundColor,
            color: theme.palette.formsComponents.chip.neutral.filled.textColor,
            ["&:hover"]: {
              backgroundColor: theme.palette.formsComponents.chip.neutral.filled.backgroundColor, // Override MUI default hover animation
            },
          }),
        ...(variant === "outlined" &&
          vibe === "neutral" && {
            backgroundColor: theme.palette.formsComponents.chip.neutral.outlined.backgroundColor,
            border: `1px solid ${theme.palette.formsComponents.chip.neutral.outlined.borderColor}`,
            color: theme.palette.formsComponents.chip.neutral.outlined.textColor,
            ["&:hover"]: {
              backgroundColor: theme.palette.formsComponents.chip.neutral.outlined.backgroundColor, // Override MUI default hover animation
            },
          }),
        ...(variant === "filled" &&
          vibe === "danger" && {
            backgroundColor: theme.palette.formsComponents.chip.danger.filled.backgroundColor,
            color: theme.palette.formsComponents.chip.danger.filled.textColor,
          }),
        ...(variant === "outlined" &&
          vibe === "danger" && {
            backgroundColor: theme.palette.formsComponents.chip.danger.outlined.backgroundColor,
            border: `1px solid ${theme.palette.formsComponents.chip.danger.outlined.borderColor}`,
            color: theme.palette.formsComponents.chip.danger.outlined.textColor,
          }),
        ...(variant === "filled" &&
          vibe === "emphasis" && {
            backgroundColor: theme.palette.formsComponents.chip.emphasis.filled.backgroundColor,
            color: theme.palette.formsComponents.chip.emphasis.filled.textColor,
          }),
        ...(variant === "outlined" &&
          vibe === "emphasis" && {
            backgroundColor: theme.palette.formsComponents.chip.emphasis.outlined.backgroundColor,
            border: `1px solid ${theme.palette.formsComponents.chip.emphasis.outlined.borderColor}`,
            color: theme.palette.formsComponents.chip.emphasis.outlined.textColor,
          }),
        ["& .MuiChip-label"]: {
          paddingInline: 0,
        },
        ["& .MuiChip-icon"]: {
          color: "inherit", // Override MUI default color
          marginInlineStart: 0,
          marginInlineEnd: 0.5,
        },
      },
      sx
    );

    return (
      <MaterialChip
        ref={ref}
        icon={
          icon && (
            <FormIcon size={components?.icon?.size ?? "xsmall"} sx={components?.icon?.sx}>
              {icon}
            </FormIcon>
          )
        }
        {...rest}
        disableRipple
        sx={styles}
      />
    );
  }
);

FormChip.displayName = "FormChip";
